/**
 * Force Status Update Modal
 *
 * Super-admin only: bulk force any selected orders to a target status.
 * Requires detailed justification (min 20 chars), optional incident ref,
 * an acknowledgement checkbox, and typing "FORCE UPDATE" to confirm.
 * Produces rich audit logs on the backend.
 */

import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from '@ufc/core'
import {
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Input,
  Label,
  Checkbox,
  Alert,
  AlertDescription,
  AlertTitle,
  Separator,
  cn,
  Loader2,
  ShieldAlert,
  AlertTriangle,
} from '@ufc/uikit'
import type { Order, OrderStatus } from '../../domain/types'

// ============================================================
// Types
// ============================================================

export interface ForceStatusUpdatePayload {
  orderIds: string[]
  newStatus: OrderStatus
  reason: string
  incidentRef?: string
  affectedOpsNote?: string
  forceJobId: string
}

interface ForceStatusUpdateModalProps {
  open: boolean
  onClose: () => void
  selectedOrders: Order[]
  onConfirm: (payload: ForceStatusUpdatePayload) => void
  isSubmitting?: boolean
}

// ============================================================
// Constants
// ============================================================

const TARGET_STATUSES = [
  { value: 'planned', labelKey: 'forceStatusUpdateModal.statusPlanned' },
  { value: 'processing', labelKey: 'forceStatusUpdateModal.statusProcessing' },
  { value: 'delivered', labelKey: 'forceStatusUpdateModal.statusDelivered' },
  { value: 'canceled', labelKey: 'forceStatusUpdateModal.statusCanceled' },
  { value: 'failed', labelKey: 'forceStatusUpdateModal.statusFailed' },
] as const

const CONFIRM_TEXT = 'FORCE UPDATE'
const MIN_REASON_LENGTH = 20

const STATUS_BADGE_CLASSES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

// ============================================================
// Helper Functions
// ============================================================

function generateForceJobId(): string {
  return `fjob_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

function countOrdersByStatus(orders: Order[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const order of orders) {
    counts[order.status] = (counts[order.status] ?? 0) + 1
  }
  return counts
}

function isFormValid(
  targetStatus: string,
  reason: string,
  acknowledged: boolean,
  confirmText: string
): boolean {
  return (
    targetStatus.length > 0 &&
    reason.trim().length >= MIN_REASON_LENGTH &&
    acknowledged &&
    confirmText === CONFIRM_TEXT
  )
}

// ============================================================
// Sub-components
// ============================================================

interface StatusSummaryProps {
  orders: Order[]
  t: (key: string) => string
}

function StatusSummary({ orders, t }: StatusSummaryProps) {
  const counts = useMemo(() => countOrdersByStatus(orders), [orders])
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(counts).map(([status, count]) => (
        <span
          key={status}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
            STATUS_BADGE_CLASSES[status] ?? 'bg-muted text-muted-foreground'
          )}
        >
          {count} × {status}
        </span>
      ))}
    </div>
  )
}

// ============================================================
// Main Component
// ============================================================

export function ForceStatusUpdateModal({
  open,
  onClose,
  selectedOrders,
  onConfirm,
  isSubmitting = false,
}: ForceStatusUpdateModalProps) {
  const { t } = useTranslation()

  const [targetStatus, setTargetStatus] = useState('')
  const [reason, setReason] = useState('')
  const [incidentRef, setIncidentRef] = useState('')
  const [affectedOpsNote, setAffectedOpsNote] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const orderCount = selectedOrders.length
  const reasonLength = reason.trim().length
  const reasonValid = reasonLength >= MIN_REASON_LENGTH
  const confirmMatch = confirmText === CONFIRM_TEXT
  const canSubmit = isFormValid(targetStatus, reason, acknowledged, confirmText)

  const handleClose = useCallback(() => {
    if (isSubmitting) return
    setTargetStatus('')
    setReason('')
    setIncidentRef('')
    setAffectedOpsNote('')
    setAcknowledged(false)
    setConfirmText('')
    onClose()
  }, [isSubmitting, onClose])

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSubmitting) return
    onConfirm({
      orderIds: selectedOrders.map((o) => o.id),
      newStatus: targetStatus as OrderStatus,
      reason: reason.trim(),
      incidentRef: incidentRef.trim() || undefined,
      affectedOpsNote: affectedOpsNote.trim() || undefined,
      forceJobId: generateForceJobId(),
    })
  }, [canSubmit, isSubmitting, onConfirm, selectedOrders, targetStatus, reason, incidentRef, affectedOpsNote])

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="h-5 w-5" />
            {t('orders.forceStatusUpdateModal.title')}
          </DialogTitle>
          <DialogDescription>
            {t('orders.forceStatusUpdateModal.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Warning alert */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('orders.forceStatusUpdateModal.warningTitle')}</AlertTitle>
            <AlertDescription>
              {t('orders.forceStatusUpdateModal.warningDesc', { count: orderCount })}
            </AlertDescription>
          </Alert>

          {/* Selected orders summary */}
          <div className="rounded-md border bg-muted/40 p-3 space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              {t('orders.forceStatusUpdateModal.ordersSummary', { count: orderCount })}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('orders.forceStatusUpdateModal.currentStatusSummaryLabel')}:
            </p>
            <StatusSummary orders={selectedOrders} t={t} />
          </div>

          <Separator />

          {/* Target status */}
          <div className="space-y-2">
            <Label htmlFor="targetStatus" className="text-sm font-medium">
              {t('orders.forceStatusUpdateModal.targetStatusLabel')}
              <span className="text-destructive ms-1">*</span>
            </Label>
            <Select value={targetStatus} onValueChange={setTargetStatus}>
              <SelectTrigger id="targetStatus" className="w-full">
                <SelectValue placeholder={t('orders.forceStatusUpdateModal.targetStatusPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {TARGET_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {t(`orders.${s.labelKey}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium">
              {t('orders.forceStatusUpdateModal.reasonLabel')}
              <span className="text-destructive ms-1">*</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('orders.forceStatusUpdateModal.reasonPlaceholder')}
              className={cn(
                'min-h-[120px] resize-y',
                !reasonValid && reason.length > 0 && 'border-destructive focus-visible:ring-destructive'
              )}
            />
            <p className={cn(
              'text-xs',
              reasonValid ? 'text-muted-foreground' : 'text-destructive'
            )}>
              {reasonLength} / {MIN_REASON_LENGTH} {!reasonValid && reason.length > 0 && `— ${t('orders.forceStatusUpdateModal.reasonMinLength')}`}
            </p>
          </div>

          {/* Incident reference */}
          <div className="space-y-2">
            <Label htmlFor="incidentRef" className="text-sm font-medium">
              {t('orders.forceStatusUpdateModal.incidentRefLabel')}
            </Label>
            <Input
              id="incidentRef"
              value={incidentRef}
              onChange={(e) => setIncidentRef(e.target.value)}
              placeholder={t('orders.forceStatusUpdateModal.incidentRefPlaceholder')}
            />
          </div>

          {/* Operational notes */}
          <div className="space-y-2">
            <Label htmlFor="affectedOpsNote" className="text-sm font-medium">
              {t('orders.forceStatusUpdateModal.affectedOpsNoteLabel')}
            </Label>
            <Textarea
              id="affectedOpsNote"
              value={affectedOpsNote}
              onChange={(e) => setAffectedOpsNote(e.target.value)}
              placeholder={t('orders.forceStatusUpdateModal.affectedOpsNotePlaceholder')}
              className="min-h-[80px] resize-y"
            />
          </div>

          <Separator />

          {/* Acknowledgement */}
          <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <Checkbox
              id="acknowledged"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
              className="mt-0.5 border-destructive data-[state=checked]:bg-destructive data-[state=checked]:border-destructive"
            />
            <Label
              htmlFor="acknowledged"
              className="text-sm leading-snug cursor-pointer text-destructive/90"
            >
              {t('orders.forceStatusUpdateModal.acknowledgeLabel')}
            </Label>
          </div>

          {/* Confirm text */}
          <div className="space-y-2">
            <Label htmlFor="confirmText" className="text-sm font-medium">
              {t('orders.forceStatusUpdateModal.confirmTextLabel')}
              <span className="text-destructive ms-1">*</span>
            </Label>
            <Input
              id="confirmText"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={t('orders.forceStatusUpdateModal.confirmTextPlaceholder')}
              className={cn(
                'font-mono tracking-wider',
                confirmText.length > 0 && !confirmMatch && 'border-destructive focus-visible:ring-destructive'
              )}
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              {t('orders.forceStatusUpdateModal.confirmTextHint')}
            </p>
            {confirmText.length > 0 && !confirmMatch && (
              <p className="text-xs text-destructive">
                {t('orders.forceStatusUpdateModal.confirmTextMismatch')}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('orders.forceStatusUpdateModal.cancelButton')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            className="gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('orders.forceStatusUpdateModal.submittingButton')}
              </>
            ) : (
              <>
                <ShieldAlert className="h-4 w-4" />
                {t('orders.forceStatusUpdateModal.submitButton', { count: orderCount })}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
